// ============================================
// COMPONENT: CalibrationModal
// ============================================
// Archivo: src/components/modals/CalibrationModal.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button } from '../common';
import { mapCalibrationFromBackend, mapCalibrationToBackend } from '../../utils/calibrationMappers';

const isImage = (path) => /\.(jpe?g|png|gif|webp|bmp)$/i.test(path || '');

const FilePreview = ({ path, label }) => {
  const [url, setUrl] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (url) { setExpanded(e => !e); return; }
    setLoading(true);
    setError(false);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/uploads/${path}`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error();
      const { url: signed } = await res.json();
      setUrl(signed);
      if (isImage(path)) setExpanded(true);
      else window.open(signed, '_blank');
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [path, url]);

  if (error) return (
    <p className="text-xs text-slate-400 mb-2">⚠️ Archivo no disponible en el servidor</p>
  );

  return (
    <div className="mb-2">
      <button type="button" onClick={load} disabled={loading}
        className="text-xs text-blue-400 hover:text-blue-300 hover:underline font-semibold disabled:opacity-50">
        {loading ? 'Cargando…' : isImage(path) ? `🖼️ ${label}` : `📄 ${label}`}
      </button>
      {expanded && url && isImage(path) && (
        <div className="mt-2">
          <img src={url} alt={label} onClick={() => window.open(url, '_blank')}
            className="max-w-full max-h-48 rounded border border-slate-500 cursor-pointer object-contain" title="Clic para ampliar" />
        </div>
      )}
    </div>
  );
};

const CalibrationModal = ({ 
  show, 
  calibration = null,
  equipment = null,
  onClose, 
  onSubmit 
}) => {
  const [formData, setFormData] = useState({
    date: '',
    nextDate: '',
    result: 'aprobado',
    observations: '',
    certificate: '',
    technician: '',
    file: null
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const isEdit = !!calibration;

  // Cargar datos si es edición
  useEffect(() => {
    if (calibration) {
      // ✅ Usar mapper para convertir de snake_case a camelCase
      const mapped = mapCalibrationFromBackend(calibration);
      setFormData({
        date: mapped.date,
        nextDate: mapped.nextDate,
        result: mapped.result,
        observations: mapped.observations,
        certificate: mapped.certificate,
        technician: mapped.technician,
        file: null
      });
    }
     else {
      // Reset en creación
      const today = new Date().toISOString().split('T')[0];
      const nextYear = new Date();
      nextYear.setFullYear(nextYear.getFullYear() + 1);
      const nextYearStr = nextYear.toISOString().split('T')[0];
      
      setFormData({
        date: today,
        nextDate: nextYearStr,
        result: 'aprobado',
        observations: '',
        certificate: '',
        technician: '',
        file: null
      });
    }
  }, [calibration, show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.date) {
      newErrors.date = 'La fecha es obligatoria';
    }

    if (!formData.nextDate) {
      newErrors.nextDate = 'La próxima fecha es obligatoria';
    }

    if (formData.date && formData.nextDate && formData.nextDate <= formData.date) {
      newErrors.nextDate = 'La próxima fecha debe ser posterior a la fecha actual';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      // Si hay archivo, usar FormData
      if (formData.file) {
        console.log('📤 Enviando con FormData porque hay archivo:', formData.file.name);
        
        // ✅ Mapear primero
        const mapped = mapCalibrationToBackend({
          ...formData,
          equipmentId: equipment?.id
        });
        
        const formDataToSend = new FormData();
        formDataToSend.append('equipmentId', mapped.equipmentId);
        formDataToSend.append('date', mapped.date);
        formDataToSend.append('nextDate', mapped.nextDate);
        formDataToSend.append('result', mapped.result);
        formDataToSend.append('observations', mapped.observations);
        formDataToSend.append('certificateNumber', mapped.certificateNumber);
        formDataToSend.append('technician', mapped.technician);
        formDataToSend.append('certificate', formData.file);  // Archivo

        await onSubmit(formDataToSend, calibration?.id);
      
      } else {
        // Sin archivo, enviar JSON normal
        console.log('📤 Enviando JSON sin archivo');
        
        // ✅ Usar mapper para convertir de camelCase a formato backend
        const submitData = mapCalibrationToBackend({
          ...formData,
          equipmentId: equipment?.id
        });
        
        console.log('📤 Datos mapeados:', submitData);
        await onSubmit(submitData, calibration?.id);
      }

      onClose();
    } catch (error) {
      console.error('Error al guardar calibración:', error);
      setErrors({ general: error.message || 'Error al guardar calibración' });
    } finally {
      setLoading(false);
    }
  };

  const resultOptions = [
    { value: 'aprobado', label: 'Aprobado' },
    { value: 'rechazado', label: 'Rechazado' },
    { value: 'condicional', label: 'Condicional' }
  ];

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={isEdit ? 'Editar Calibración' : 'Registrar Calibración'}
      maxWidth="2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Información del equipo */}
        {equipment && (
          <div className="bg-slate-700 p-4 rounded-lg mb-4">
            <p className="text-sm text-slate-300">
              <span className="font-semibold">Equipo:</span> {equipment.name}
            </p>
            <p className="text-sm text-slate-300">
              <span className="font-semibold">Código:</span> {equipment.code || 'Sin código'}
            </p>
          </div>
        )}

        {/* Error general */}
        {errors.general && (
          <div className="bg-red-500/10 border border-red-500 text-red-400 px-4 py-2 rounded">
            {errors.general}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          {/* Fecha de calibración */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Fecha de Calibración <span className="text-red-400">*</span>
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {errors.date && (
              <p className="mt-1 text-sm text-red-400">{errors.date}</p>
            )}
          </div>

          {/* Próxima calibración */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Próxima Calibración <span className="text-red-400">*</span>
            </label>
            <input
              type="date"
              name="nextDate"
              value={formData.nextDate}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {errors.nextDate && (
              <p className="mt-1 text-sm text-red-400">{errors.nextDate}</p>
            )}
          </div>
        </div>

        {/* Resultado */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Resultado <span className="text-red-400">*</span>
          </label>
          <select
            name="result"
            value={formData.result}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {resultOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Certificado */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Número de Certificado
          </label>
          <input
            type="text"
            name="certificate"
            value={formData.certificate}
            onChange={handleChange}
            placeholder="Ej: CERT-2024-001"
            className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Técnico */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Técnico Responsable
          </label>
          <input
            type="text"
            name="technician"
            value={formData.technician}
            onChange={handleChange}
            placeholder="Ej: Juan Pérez"
            className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Archivo de certificado */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            {isEdit ? 'Certificado' : 'Subir Certificado'}
          </label>
          {isEdit && calibration?.archivo_path && !formData.file && (
            <FilePreview path={calibration.archivo_path} label="Ver certificado actual" />
          )}
          <input
            type="file"
            accept=".pdf,image/*"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) setFormData(prev => ({ ...prev, file }));
            }}
            className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-slate-400">
            {formData.file
              ? `Nuevo: ${formData.file.name}`
              : isEdit && calibration?.archivo_path
                ? 'Selecciona un archivo para reemplazar el actual'
                : 'Formato: PDF o imagen, máximo 5MB'}
          </p>
        </div>

        {/* Observaciones */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Observaciones
          </label>
          <textarea
            name="observations"
            value={formData.observations}
            onChange={handleChange}
            rows={4}
            className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Observaciones adicionales..."
          />
        </div>

        {/* Botones */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading}
          >
            {loading ? 'Guardando...' : (isEdit ? 'Actualizar' : 'Registrar')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CalibrationModal;